import { z } from "zod";
import { emailOtpRequestSchema, emailOtpVerifySchema } from "./auth-otp.js";

/**
 * Shared DTO — gắn email cho tài khoản social CHƯA có email, CÓ verify OTP
 * (US-E24.F17-0366-S01 / @UC9620; QĐ PO 06/08/2026).
 *
 * Vì sao cần: user đăng nhập Facebook có thể `users.email = NULL` (Graph không trả email khi app
 * chưa có Advanced Access, và user vẫn có quyền từ chối cấp email ở màn consent). Không email ⇒
 * không `IdentityIndex(email)` ⇒ **vô hình với mọi lời mời** (repo cấm SMS ⇒ email là khoá match
 * duy nhất còn lại), và tự "sửa" bằng đăng nhập OTP thì **đẻ tài khoản thứ hai** — mà tiền neo vào
 * member nên tách đôi danh tính là tách đôi nghĩa vụ tiền.
 *
 * 🔴 TÁI DÙNG body của email-OTP đăng nhập (`auth-otp.ts`) — KHÔNG hợp đồng thứ hai. Hai bản sao
 * của cùng một luật sẽ lệch ở lần sửa đầu tiên. Điểm khác duy nhất là RESPONSE: đường này không
 * phát phiên (user đã đăng nhập), nó trả khoá danh tính vừa gắn.
 */

/** Body `POST /v1/me/email/verify-request` — y nguyên schema email của email-OTP (trim+lowercase). */
export const attachEmailRequestSchema = emailOtpRequestSchema;
export type AttachEmailRequest = z.infer<typeof attachEmailRequestSchema>;

/** Body `POST /v1/me/email/verify` — y nguyên schema {email, code 6 số} của email-OTP. */
export const attachEmailVerifySchema = emailOtpVerifySchema;
export type AttachEmailVerifyRequest = z.infer<typeof attachEmailVerifySchema>;

/** Response bước 1 — LUÔN cùng shape (không lộ khác biệt trạng thái hòm thư). */
export const ATTACH_EMAIL_SENT_STATUS = "sent" as const;

export interface AttachEmailRequestResponse {
  status: typeof ATTACH_EMAIL_SENT_STATUS;
}

/**
 * Response bước 2. C3: TUYỆT ĐỐI không mang token/mã OTP — đường này KHÔNG phát phiên mới,
 * phiên hiện tại của user vẫn nguyên. `.strict()` để trạng thái sai không biểu diễn được.
 */
export const attachEmailResponseSchema = z
  .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    emailVerifiedAt: z.string().datetime(),
  })
  .strict();

export type AttachEmailResponse = z.infer<typeof attachEmailResponseSchema>;

/**
 * Mã lỗi ổn định, ngôn-ngữ-trung-tính (client map code→message).
 *
 * `EMAIL_ALREADY_SET` = tài khoản ĐANG đăng nhập đã có email ⇒ 409. Task này chỉ gắn cho
 * `users.email IS NULL`; **đổi** email đã verify là bài toán khác (phải thu hồi quyền truy cập từ
 * hòm thư cũ, nếu không thì mất hòm thư = mất tài khoản) ⇒ task riêng (QĐ PO §7c).
 *
 * Email đã thuộc **user khác** dùng lại `EMAIL_ALREADY_REGISTERED` của tầng auth — cùng một sự thật
 * ("khoá này có chủ"), không đẻ mã thứ hai.
 */
export const EMAIL_ALREADY_SET_CODE = "EMAIL_ALREADY_SET";
export const EMAIL_ALREADY_SET_MESSAGE = "Tài khoản của bạn đã có email";
